import { useState } from 'react';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { useTranslation } from '../../i18n/index.jsx';
import { useTheme } from '../../context/ThemeContext';
import Icon from '../common/Icon';

export default function LoginScreen({ onLogin, users, setUsers }) {
  const { t, language, setLanguage } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [started, setStarted] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  const [verifying, setVerifying] = useState(false);

  const handlePinInput = async (num) => {
    if (pin.length >= 4 || verifying) return;
    const newPin = pin + num;
    setPin(newPin);
    setError('');
    if (newPin.length < 4 || !selectedUser) return;
    setVerifying(true);
    try {
      const res = await fetch('/api/auth/verify-pin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: selectedUser.id, pin: newPin }),
      });
      const result = await res.json();
      if (result.ok && result.customToken) {
        await signInWithCustomToken(auth, result.customToken);
        onLogin(result.user ?? { ...selectedUser });
      } else {
        setError(t('login.wrongPin'));
        setPin('');
      }
    } catch (err) {
      setError('ネットワークエラー');
      setPin('');
    } finally {
      setVerifying(false);
    }
  };

  const handleBack = () => {
    if (selectedUser) {
      setSelectedUser(null);
      setPin('');
      setError('');
    } else {
      setStarted(false);
    }
  };

  const getRoleLabel = (user) => {
    if (user.role === 'admin') return t('login.admin');
    if ((user.name || '').includes('柚木') || (user.name || '').includes('Yuki')) return t('login.storeManager');
    return t('login.staff');
  };

  const getRoleStyle = (user) => {
    if (user.role === 'admin') return 'bg-purple-500/20 text-purple-400';
    if ((user.name || '').includes('柚木') || (user.name || '').includes('Yuki')) return 'bg-emerald-500/20 text-emerald-400';
    return 'bg-blue-500/20 text-blue-400';
  };

  return (
    <div className="min-h-screen bg-t-bg flex flex-col items-center justify-center p-6 relative">

      {/* Language + theme switcher - top right */}
      <div className="absolute top-4 right-4 flex gap-2 items-center">
        <button onClick={toggleTheme} className="w-9 h-9 rounded-full flex items-center justify-center text-lg bg-t-card opacity-70 hover:opacity-100 transition-all">
          <Icon name={theme === 'dark' ? 'Sun' : 'Moon'} size={18} className="text-t-secondary" />
        </button>
        <button onClick={() => setLanguage('ja')} className={`w-9 h-9 rounded-full flex items-center justify-center text-lg transition-all ${language === 'ja' ? 'bg-t-accent-soft ring-2 ring-[var(--theme-accent)] scale-110' : 'bg-t-card opacity-50 hover:opacity-80'}`}>🇯🇵</button>
        <button onClick={() => setLanguage('en')} className={`w-9 h-9 rounded-full flex items-center justify-center text-lg transition-all ${language === 'en' ? 'bg-t-accent-soft ring-2 ring-[var(--theme-accent)] scale-110' : 'bg-t-card opacity-50 hover:opacity-80'}`}>🇬🇧</button>
      </div>

      <div className="text-center mb-8 animate-fadeIn">
        <div className="text-5xl mb-4">🍷</div>
        <h1 className="text-2xl font-bold text-t-primary">{t('login.title')}</h1>
      </div>

      {!started ? (
        /* Start按钮 */
        <div className="w-full max-w-sm animate-fadeIn">
          <button onClick={() => setStarted(true)} className="w-full bg-t-accent text-t-bg font-bold py-5 rounded-2xl text-xl shadow-lg hover:bg-t-accent-hover transition-all transform hover:scale-[1.02] active:scale-[0.98]">
            {t('login.start')}
          </button>
        </div>
      ) : !selectedUser ? (
        /* 选择用户 */
        <div className="w-full max-w-sm animate-fadeIn">
          <button onClick={handleBack} className="text-t-secondary mb-4 flex items-center hover:text-t-primary">
            <Icon name="ArrowLeft" size={18} className="mr-1" /> {t('login.back')}
          </button>

          <div className="bg-t-card border border-t-border rounded-2xl p-6">
            <h2 className="text-t-primary font-bold mb-4 text-center">{t('login.selectUser')}</h2>
            <div className="space-y-2">
              {users.map(user => (
                <button key={user.id} onClick={() => setSelectedUser(user)} className="w-full p-4 rounded-xl flex items-center transition bg-t-input border border-transparent hover:border-[var(--theme-accent)]">
                  <span className="text-2xl mr-3">{user.avatar}</span>
                  <span className="text-t-primary flex-1 text-left">{user.name}</span>
                  <span className={`text-xs px-2 py-1 rounded ${getRoleStyle(user)}`}>
                    {getRoleLabel(user)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        /* PIN输入 */
        <div className="w-full max-w-sm animate-fadeIn">
          <button onClick={handleBack} className="text-t-secondary mb-4 flex items-center hover:text-t-primary">
            <Icon name="ArrowLeft" size={18} className="mr-1" /> {t('login.back')}
          </button>

          <div className="bg-t-card border border-t-border rounded-2xl p-6">
            <div className="text-center mb-4">
              <span className="text-3xl">{selectedUser.avatar}</span>
              <h2 className="text-t-primary font-bold mt-2">{selectedUser.name}</h2>
              <p className="text-t-secondary text-sm">{t('login.enterPin')}</p>
            </div>

            <div className="flex justify-center gap-3 mb-6">
              {[0,1,2,3].map(i => (
                <div key={i} className={`w-12 h-12 rounded-xl border-2 flex items-center justify-center text-xl ${pin.length > i ? 'border-[var(--theme-accent)] bg-t-accent-soft' : 'border-t-border'}`}>
                  {pin.length > i && '●'}
                </div>
              ))}
            </div>

            {error && <p className="text-red-500 text-sm mb-4 text-center">{error}</p>}

            <div className="grid grid-cols-3 gap-3">
              {[1,2,3,4,5,6,7,8,9,'',0,'⌫'].map((num, i) => (
                <button key={i} onClick={() => num === '⌫' ? setPin(pin.slice(0,-1)) : num !== '' && handlePinInput(String(num))} disabled={num === ''} className={`h-14 rounded-xl font-bold text-xl transition ${num === '' ? '' : num === '⌫' ? 'bg-t-input text-t-tertiary hover:bg-t-surface' : 'bg-t-input text-t-primary hover:bg-t-surface active:bg-t-accent active:text-t-bg'}`}>
                  {num}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <p className="text-t-muted text-xs mt-8">v5.0</p>
    </div>
  );
}
